import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  ClickAwayListener,
  IconButton,
  Paper,
  Popper,
  Stack,
  Typography,
} from '@mui/material';

import { DEFAULT_MAX_WIDTH, DEFAULT_WIDTH } from './constants';
import { stopEvent } from './utils';
import type { OptionsEditorPopoverShellProps } from './types';
import CloseIcon from '@assets/icons/CloseIcon';
import { Theme } from '@modules/common/types/general';
import { UserPreferenceName } from '@/modules/userPreferences';
import { useUserPreference } from '@/modules/userPreferences/hooks';
import { StyledTooltip } from '@/modules/common/components/StyledTooltip';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import { useAppStore } from '@/modules/app/store/useAppStore';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';

function OptionsEditorPopoverShellComponent({
  disabled,
  ariaLabel,
  icon,
  title,
  width = DEFAULT_WIDTH,
  maxWidth = DEFAULT_MAX_WIDTH,
  onClose,
  children,
}: OptionsEditorPopoverShellProps) {
  const { t } = useTranslation('common');
  const theme = useUserPreference(UserPreferenceName.GENERAL_THEME);
  const instanceId = useRef(uuid());
  // plz fix me
  // eslint-disable-next-line react-hooks/refs
  const appSvc = useRef(AppServiceFactory.getInstance().createAppService()).current;
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  const activeId = useAppStore((s) => s.activePopoverIdState);

  const onCloseInner = useCallback(() => {
    setAnchorEl(null);
    if (activeId === instanceId.current) appSvc.setActivePopoverId(null);
    onClose?.();
  }, [activeId, onClose, appSvc]);

  const onOpen = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      // If this one is active already: toggle closed.
      if (activeId === instanceId.current) {
        onCloseInner();
        return;
      }

      setAnchorEl(event.currentTarget);
      appSvc.setActivePopoverId(instanceId.current);
    },
    [activeId, onCloseInner, appSvc],
  );

  useEffect(() => {
    const id = instanceId.current;
    return () => {
      // If unmounting while active, clear active id.
      if (useAppStore.getState().activePopoverIdState === id) {
        appSvc.setActivePopoverId(null);
      }
    };
  }, [appSvc]);

  useEffect(() => {
    // If another popover becomes active, close this one.
    if (activeId !== instanceId.current) {
      setAnchorEl(null);
    }
  }, [activeId]);

  return (
    <>
      <IconButton
        size='small'
        disabled={disabled}
        onMouseDown={stopEvent}
        onClick={(e) => {
          e.stopPropagation();
          onOpen(e);
        }}
        aria-label={ariaLabel}
        aria-expanded={open}
      >
        {icon}
      </IconButton>

      <Popper
        anchorEl={anchorEl}
        open={open}
        placement='left-start'
        sx={{
          width,
          maxWidth,
        }}
        modifiers={[
          {
            name: 'offset',
            options: {
              offset: [0, 280],
            },
          },
          {
            name: 'preventOverflow',
            options: {
              tether: false,
              altAxis: true,
            },
          },
        ]}
        className={theme === Theme.DARK ? 'dark' : ''}
      >
        <ClickAwayListener onClickAway={onCloseInner}>
          <Paper sx={{ '&:hover': { cursor: 'default' }, padding: 2 }}>
            <Stack gap={1}>
              <Stack direction='row' alignItems='center' gap={1} justifyContent='space-between'>
                <Box>
                  <Typography variant='h4'>{title}</Typography>
                </Box>
                <StyledTooltip title={t('close')}>
                  <IconButton onClick={onCloseInner} sx={{ alignSelf: 'start' }}>
                    <CloseIcon />
                  </IconButton>
                </StyledTooltip>
              </Stack>
              <Box>{children}</Box>
            </Stack>
          </Paper>
        </ClickAwayListener>
      </Popper>
    </>
  );
}

export const OptionsEditorPopoverShell = memo(OptionsEditorPopoverShellComponent);
